import { activateLanguage, getLocale, getRequestedStoreLanguage, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { useEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { isAxiosError } from "axios";
import { ClockCircleOutlined, MailOutlined, MobileOutlined, LinkOutlined, DisconnectOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { getTenantInfo } from "../storefrontService.ts";
import CustomerAuthShell from "./CustomerAuthShell.tsx";
import LoadingRing from "../../shared/LoadingRing.jsx";
import { agencyContext, agencyInvitationContext, acceptAgencyInvitation, sendAgencyVerification, verifyAgencyInvitationCode } from "./agencyIdentityService.ts";
import { normalizeLoginDigits } from "./customerIdentityService.ts";
import { agencyRegistrationPasswordRules, normalizeAgencyPassword } from "./agencyPasswordPolicy.ts";
import { saveRegistrationCredential } from "./saveRegistrationCredential.ts";
import AgencyVerificationCodeInput from "./AgencyVerificationCodeInput.tsx";
import "./CustomerLogin.css";
import "./AgencyInvitationPage.css";

type InvitationValues = {
  displayName: string;
  mobile: string;
  verificationCode: string;
  password: string;
  passwordConfirmation: string;
};

const MOBILE_PATTERN = /^09\d{9}$/;

function normalizeMobile(value: string): string {
  if (value.includes("@")) return value.trim().toLowerCase();
  return normalizeLoginDigits(value).trim().replace(/^(?:\+98|0098)/, "0");
}

export default function AgencyInvitationPage() {
  const location = useLocation();
  const requestedLanguage = getRequestedStoreLanguage(location.search);
  return (
    <AgencyInvitationEntry
      key={`${location.hash}:${requestedLanguage || ""}`}
      token={location.hash.slice(1)}
      requestedLanguage={requestedLanguage}
    />
  );
}

function AgencyInvitationEntry({ token, requestedLanguage }: { token: string; requestedLanguage: string | null }) {
  const storeLanguage = useStoreLanguage();
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [method, setMethod] = useState<"email" | "sms">("sms");
  const [status, setStatus] = useState<"loading" | "ready" | "invalid" | "unavailable">("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(function () {
    if (requestedLanguage) activateLanguage(requestedLanguage);
  }, [requestedLanguage]);

  useEffect(function () {
    let active = true;
    getTenantInfo({ notifyErrors: false, anonymous: true }).then(function (info) {
      if (active) setCompanyInfo(info);
    }).catch(function () {
      // The invitation status remains useful if public branding is unavailable.
    });
    return function () { active = false; };
  }, [storeLanguage]);

  useEffect(function () {
    let active = true;
    if (!/^[0-9a-fA-F]{64}$/.test(token)) {
      setStatus("invalid");
      return;
    }
    setStatus("loading");
    agencyInvitationContext(token).then(function (context) {
      if (!active) return;
      activateLanguage(requestedLanguage || context.languageCode);
      setMethod(context.method);
      setStatus("ready");
    }).catch(function (error) {
      if (!active) return;
      // Only the context endpoint's business rejection establishes invalidity.
      const invalid = isAxiosError(error)
        && error.response?.status === 400
        && error.config?.url?.endsWith("/invitation-context");
      setStatus(invalid ? "invalid" : "unavailable");
    });
    return function () { active = false; };
  }, [token, requestedLanguage, attempt]);

  if (status === "ready") {
    return (
      <AgencyInvitationForm
        companyInfo={companyInfo}
        emailMode={method === "email"}
      />
    );
  }

  const invalid = status === "invalid";
  const loading = status === "loading";
  return (
    <CustomerAuthShell
      companyInfo={companyInfo}
      title={loading ? t("در حال بررسی دعوت‌نامه") : invalid ? t("این دعوت‌نامه معتبر نیست") : t("بررسی دعوت‌نامه ممکن نشد")}
      description={loading ? "" : invalid ? t("با این لینک نمی‌توانید ثبت‌نام را ادامه دهید.") : t("در حال حاضر امکان ارتباط با سامانه وجود ندارد. کمی بعد دوباره تلاش کنید.")}
      variant="invitation-status"
      statusIcon={loading ? undefined : invalid ? <LinkOutlined /> : <DisconnectOutlined />}
    >
      {loading ? <LoadingRing tip={t("در حال بررسی دعوت‌نامه")} /> : (
        <div className="customer-invitation-status">
          {invalid ? (
            <div className="customer-invitation-status-guidance">
              <h2>{t("دعوت‌نامه جدید دریافت کنید")}</h2>
              <p>{t("از فرستنده دعوت‌نامه بخواهید لینک جدیدی برایتان ارسال کند، سپس همان لینک را باز کنید.")}</p>
            </div>
          ) : (
            <Button
              type="primary"
              block
              onClick={function () {
                setStatus("loading");
                setAttempt(function (value) { return value + 1; });
              }}
            >
              {t("تلاش دوباره")}
            </Button>
          )}
        </div>
      )}
    </CustomerAuthShell>
  );
}

function AgencyInvitationForm({ emailMode, companyInfo }: { emailMode: boolean; companyInfo: any }) {
  const storeLanguage = useStoreLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const token = location.hash.slice(1);
  const [form] = Form.useForm<InvitationValues>();
  const watchedMobile = Form.useWatch("mobile", form);
  const mobile = normalizeMobile(watchedMobile || "");
  const [requestedMobile, setRequestedMobile] = useState("");
  const verificationCode = Form.useWatch("verificationCode", form);
  const hasCompleteCode = /^\d{6}$/.test(verificationCode ?? "");
  const [passwordStep, setPasswordStep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [codeRequestedAt, setCodeRequestedAt] = useState<number | null>(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(0);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [now, setNow] = useState(Date.now);
  const remainingSeconds = codeRequestedAt === null ? 0 : Math.max(0, Math.ceil((codeExpiresAt - now) / 1000));
  const resendSeconds = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
  const codeExpired = codeRequestedAt !== null && remainingSeconds === 0;
  const validToken = /^[0-9a-fA-F]{64}$/.test(token);
  const locked = useRef(false);

  function formatTime(seconds: number) {
    const numbers = new Intl.NumberFormat(getLocale(), { minimumIntegerDigits: 2, useGrouping: false });
    return `${numbers.format(Math.floor(seconds / 60))}:${numbers.format(seconds % 60)}`;
  }

  useEffect(function () {
    if (codeRequestedAt === null && resendAvailableAt === 0) return;
    function updateClock() { setNow(Date.now()); }
    const interval = window.setInterval(updateClock, 1000);
    document.addEventListener("visibilitychange", updateClock);
    return function () {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", updateClock);
    };
  }, [codeRequestedAt, resendAvailableAt]);

  useEffect(function () {
    setPasswordStep(false);
    setCodeRequestedAt(null);
    setResendAvailableAt(0);
    form.resetFields();
  }, [token, form]);

  async function sendCode() {
    if (locked.current || resendSeconds > 0 || !validToken) return;
    const requestedAt = Date.now();
    locked.current = true;
    setSending(true);
    try {
      let recipient = passwordStep ? requestedMobile : "";
      if (!recipient) {
        const values = await form.validateFields(["mobile"]);
        recipient = normalizeMobile(values.mobile);
      }
      const timing = await sendAgencyVerification(token, recipient);
      const serverTime = Date.parse(timing.serverTime);
      const expiresAt = Date.parse(timing.expiresAt);
      const retryAt = Date.parse(timing.retryAt);
      if (![serverTime, expiresAt, retryAt].every(Number.isFinite)) {
        throw new Error(t("اطلاعات زمان اعتبار کد دریافت نشد؛ دوباره تلاش کنید."));
      }
      form.setFieldsValue({
        mobile: recipient,
        verificationCode: "",
      });
      setRequestedMobile(recipient);
      // Use the server's original expiry, including on resend; never restart four minutes locally.
      setCodeRequestedAt(requestedAt);
      setCodeExpiresAt(requestedAt + Math.max(0, expiresAt - serverTime));
      setResendAvailableAt(requestedAt + Math.max(0, retryAt - serverTime));
      setNow(Date.now());
      setPasswordStep(false);
      if (timing.deliveryFailed) {
        AppFeedback.error({
          title: timing.deliveryFailureMessage
            ? t("خطا!")
            : t("ارسال کد انجام نشد؛ برای دریافت کد با شرکت تماس بگیرید."),
          description: timing.deliveryFailureMessage || undefined,
        });
      } else AppFeedback.info({
        title: t("کد تأیید ارسال شد"),
      });
    } catch (error) {
      if (error && typeof error === "object" && "errorFields" in error) {
        AppFeedback.error({ title: emailMode ? t("آدرس ایمیل معتبر وارد کنید.") : t("شماره همراه معتبر وارد کنید.") });
      } else {
        AppFeedback.httpError(error);
      }
    }
    finally { locked.current = false; setSending(false); }
  }

  async function verifyCode(code: string) {
    if (locked.current || !validToken || codeRequestedAt === null || mobile !== requestedMobile
        || !/^\d{6}$/.test(code)) return;
    if (Date.now() >= codeExpiresAt) {
      setNow(Date.now());
      AppFeedback.error({ title: t("زمان کد تمام شد؛ کد جدید دریافت کنید.") });
      return;
    }
    locked.current = true;
    setBusy(true);
    try {
      const verified = await verifyAgencyInvitationCode(token, requestedMobile, code);
      if (verified) {
        setPasswordStep(true);
      } else {
        AppFeedback.error({ title: t("کد تأیید معتبر وارد کنید.") });
      }
    } catch (error) {
      AppFeedback.httpError(error);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  async function accept(values: InvitationValues) {
    if (!passwordStep) {
      await verifyCode(normalizeLoginDigits(values.verificationCode || ""));
      return;
    }
    if (locked.current || !validToken || codeRequestedAt === null || mobile !== requestedMobile) return;
    if (codeRequestedAt !== null && Date.now() >= codeExpiresAt) {
      setNow(Date.now());
      AppFeedback.error({ title: t("زمان کد تمام شد؛ کد جدید دریافت کنید.") });
      return;
    }
    locked.current = true;
    setBusy(true);
    try {
      const result = await acceptAgencyInvitation({
        token,
        mobile: normalizeMobile(values.mobile),
        verificationCode: normalizeLoginDigits(values.verificationCode),
        displayName: values.displayName.trim(),
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      });
      saveRegistrationCredential(normalizeMobile(values.mobile), values.password, values.displayName.trim());
      if (!result.isAuthenticated) {
        AppFeedback.success({ title: t("ثبت‌نام انجام شد؛ برای انتخاب نماینده وارد شوید.") });
        navigate(`/login?language=${encodeURIComponent(storeLanguage)}`, { replace: true });
        return;
      }
      await agencyContext();
      localStorage.removeItem("jwt");
      localStorage.removeItem("user");
      navigate("/products", { replace: true });
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400
          && error.config?.url?.endsWith("/accept-invitation")) {
        setPasswordStep(false);
      }
      AppFeedback.httpError(error);
    }
    finally { locked.current = false; setBusy(false); }
  }

  return (
    <CustomerAuthShell
      companyInfo={companyInfo}
      title={passwordStep ? t("تکمیل ثبت‌نام") : t("ثبت‌نام در پرتال")}
      description={passwordStep ? t("برای ورودهای بعدی، رمز عبور دلخواه خود را تعیین کنید.") : ""}
      variant="invitation"
    >
      <Form
        form={form}
        name="invitation-registration"
        autoComplete="on"
        layout="vertical"
        requiredMark={false}
        className="customer-auth-form customer-invitation-form"
        onFinish={accept}
        onFinishFailed={function () { AppFeedback.error({ title: t("اطلاعات فرم را کامل کنید.") }); }}
      >
        <ol
          className="customer-invitation-steps"
          aria-label={t("مراحل ثبت‌نام")}
        >
          <li
            className={passwordStep ? "is-complete" : "is-current"}
            aria-current={passwordStep ? undefined : "step"}
          >
            <span className="customer-invitation-step-number" aria-hidden="true">{new Intl.NumberFormat(getLocale()).format(1)}</span>
            <span>{emailMode ? t("تأیید ایمیل") : t("تأیید شماره")}</span>
          </li>
          <li
            className={passwordStep ? "is-current" : ""}
            aria-current={passwordStep ? "step" : undefined}
          >
            <span className="customer-invitation-step-number" aria-hidden="true">{new Intl.NumberFormat(getLocale()).format(2)}</span>
            <span>{t("ساخت حساب")}</span>
          </li>
          <li>
            <span className="customer-invitation-step-number" aria-hidden="true">{new Intl.NumberFormat(getLocale()).format(3)}</span>
            <span>{t("ورود به پورتال")}</span>
          </li>
        </ol>
        <Form.Item
          name="mobile"
          hidden={passwordStep}
          label={emailMode ? t("ایمیل") : t("شماره موبایل")}
          extra={emailMode ? t("همان ایمیلی را وارد کنید که دعوت‌نامه به آن ارسال شده است.") : undefined}
          rules={[
            { required: true, message: emailMode ? t("ایمیل را وارد کنید.") : t("شماره موبایل را وارد کنید") },
            {
              validator: function (_, value?: string) {
                if (!value || (emailMode ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) : MOBILE_PATTERN.test(normalizeMobile(value)))) return Promise.resolve();
                return Promise.reject(new Error(emailMode ? t("آدرس ایمیل معتبر وارد کنید.") : t("شماره موبایل معتبر نیست؛ مانند 09123456789")));
              },
            },
          ]}
        >
          <Input
            prefix={emailMode ? <MailOutlined /> : <MobileOutlined />}
            placeholder={emailMode ? "you@example.com" : t("مثلاً 09123456789")}
            inputMode={emailMode ? "email" : "tel"}
            name="username"
            autoComplete="username"
            disabled={busy || sending}
            dir="ltr"
            onChange={function (event) {
              if (normalizeMobile(event.target.value) === mobile) return;
              setRequestedMobile("");
              setCodeRequestedAt(null);
              form.setFieldValue("verificationCode", "");
            }}
          />
        </Form.Item>
        {codeRequestedAt !== null && mobile === requestedMobile && (
          <Form.Item
            name="verificationCode"
            hidden={passwordStep}
            className="customer-invitation-code-field"
            label={emailMode ? t("کد تأیید ایمیل") : t("کد تأیید شمارهٔ همراه")}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: emailMode ? t("کد ایمیلی را وارد کنید.") : t("کد پیامکی را وارد کنید.") },
              { pattern: /^\d{6}$/, message: t("کد تأیید باید شش رقم باشد.") },
            ]}
          >
            <AgencyVerificationCodeInput
              focusRequest={passwordStep ? undefined : codeRequestedAt}
              disabled={busy || sending || codeExpired}
              onComplete={function (code) {
                void verifyCode(code);
              }}
            />
          </Form.Item>
        )}
        {passwordStep ? (
          <>
            <Form.Item
              name="displayName"
              label={t("نام و نام خانوادگی")}
              rules={[
                { required: true, whitespace: true, message: t("نام خود را وارد کنید.") },
                { max: 200, message: t("نام حداکثر ۲۰۰ نویسه است.") },
              ]}
            >
              <Input
                autoComplete="name"
                autoFocus
                maxLength={200}
                disabled={busy || sending}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label={t("رمز عبور")}
              extra={t("رمز عبور باید حداقل ۸ کاراکتر باشد.")}
              rules={agencyRegistrationPasswordRules()}
            >
              <Input.Password
                name="password"
                autoComplete="new-password"
                disabled={busy || sending}
                dir="ltr"
              />
            </Form.Item>
            <Form.Item
              name="passwordConfirmation"
              label={t("تکرار رمز عبور")}
              dependencies={["password"]}
              rules={[
                { required: true, message: t("تکرار رمز عبور را وارد کنید.") },
                {
                  validator: function (_, value?: string) {
                    if (!value || normalizeAgencyPassword(value)
                      === normalizeAgencyPassword(form.getFieldValue("password"))) return Promise.resolve();
                    return Promise.reject(new Error(t("رمز عبور و تکرار آن یکسان نیستند.")));
                  },
                },
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                disabled={busy || sending}
                dir="ltr"
              />
            </Form.Item>
          </>
        ) : null}
        <div className="customer-invitation-code-action">
          {(!passwordStep || codeExpired) && (
            <Button
              type={codeRequestedAt === null && !hasCompleteCode ? "primary" : "default"}
              block
              onClick={sendCode}
              loading={sending}
              disabled={busy || sending || resendSeconds > 0 || !validToken}
            >
              {resendSeconds > 0
                ? t("ارسال مجدد پس از {{seconds}} ثانیه", { seconds: resendSeconds })
                : codeRequestedAt !== null ? t("ارسال مجدد کد تأیید") : t("دریافت کد تأیید")}
            </Button>
          )}
          {codeRequestedAt !== null && (
            <div className={`customer-invitation-code-status${codeExpired ? " is-expired" : ""}`}>
              <div className="customer-invitation-countdown" role="timer" aria-live="off">
                <ClockCircleOutlined />
                <span>{t("اعتبار کد")}</span>
                <bdi>{formatTime(remainingSeconds)}</bdi>
              </div>
              {(codeExpired || (!passwordStep && resendSeconds === 0)) && (
                <span
                  className="customer-invitation-resend-hint"
                  role={codeExpired ? "status" : undefined}
                >
                  {codeExpired
                    ? t("زمان کد تمام شد؛ کد جدید دریافت کنید.")
                    : t("کد را دریافت نکردید؟ دوباره ارسال کنید.")}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="customer-auth-actions-group customer-invitation-actions">
          {hasCompleteCode && !codeExpired && (
            <Form.Item className="customer-auth-submit">
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={busy}
                disabled={busy || sending || codeExpired || !validToken || mobile !== requestedMobile}
              >
                {passwordStep ? t("تکمیل ثبت‌نام") : t("ادامه")}
              </Button>
            </Form.Item>
          )}
          <div className="customer-auth-register-prompt">
            <span>{t("حساب دارید؟")}</span>
            <button
              type="button"
              className="customer-auth-register-link"
              onClick={function () { navigate(`/login?language=${encodeURIComponent(storeLanguage)}`); }}
            >
              {t("ورود به پورتال نمایندگان")}
            </button>
          </div>
        </div>
        {!validToken && (
          <p className="customer-invitation-link-error">
            {t("لینک ثبت‌نام کامل نیست؛ آن را دوباره از پیام دریافتی باز کنید.")}
          </p>
        )}
      </Form>
    </CustomerAuthShell>
  );
}
